/**
 * Servicio para conectarse al backend de NumeCALENDAR
 * Reemplaza el servicio gemini.js para usar el backend real con caché
 */

import { getOrCreateProfileId, getOrCreateNinjaProfileId } from "../utils/uuid";

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export async function fetchPublicPlans() {
  try {
    const response = await fetch(`${API_BASE_URL}/plans/public`, {
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}`);
    }

    const data = await response.json();
    if (!data || !Array.isArray(data.plans)) {
      return { plans: [], success: false };
    }

    return data;
  } catch (error) {
    console.error('❌ Error obteniendo planes públicos:', error);
    return {
      success: false,
      plans: [],
      error: error.message
    };
  }
}

/**
 * Obtener token de autenticación del localStorage
 */
function getAuthToken() {
  // Obtener token real desde localStorage
  return localStorage.getItem('auth_token') || '';
}

/**
 * Generar mensaje personalizado para una categoría específica
 * @param {string} category - 'work', 'love', 'wellness', 'spiritual'
 * @param {object} numerologyData - Datos numerológicos del día
 * @param {object} userData - Datos del usuario
 * @returns {Promise<string>} - Mensaje generado
 */
export async function generatePersonalizedMessage(category, numerologyData, userData) {
  try {
    const { personalDay, universalDay, date } = numerologyData;
    
    // Si no viene fecha, usar la de hoy como fallback
    const targetDate = date || new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Determinar profileId (main o ninja) con prioridad a IDs reales
    let profileId = userData?.profileId || userData?.id || userData?.userId || null;
    let ninjaProfileId = null;

    if (userData?.isNinja) {
      const ninjaKey = `${userData?.name || 'ninja'}_${userData?.birth || ''}`;
      ninjaProfileId = userData?.profileId || userData?.id || getOrCreateNinjaProfileId(ninjaKey);
      profileId = ninjaProfileId;
    }

    if (!profileId) {
      profileId = getOrCreateProfileId();
    }
    
    const response = await fetch(`${API_BASE_URL}/messages/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({
        profileId,
        ninjaProfileId,
        date: targetDate,
        category,
        personalDay,
        universalDay,
        userData: {
          name: userData.name || 'Usuario',
          birth: userData.birth || '1990-01-01',
          occupation: userData.occupation || ['profesional'],
          status: userData.status || 'soltero/a'
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    
    console.log(`✅ Mensaje ${category}:`, {
      cached: data.cached || data.source === 'cache',
      tokens: data.tokensUsed || data.tokens,
      model: data.model
    });
    
    return data.message;
    
  } catch (error) {
    console.error(`❌ Error generando mensaje ${category}:`, error);
    
    // Fallback: mensaje genérico si el backend falla
    return `⚠️ No pudimos generar el mensaje personalizado en este momento. Por favor, intenta más tarde.`;
  }
}

/**
 * Generar mensajes para múltiples categorías en paralelo
 * @param {Array<string>} categories - ['work', 'love', 'wellness', 'spiritual']
 * @param {object} numerologyData - Datos numerológicos
 * @param {object} userData - Datos del usuario
 * @returns {Promise<object>} - Objeto con mensajes por categoría
 */
export async function generateBatchMessages(categories, numerologyData, userData) {
  try {
    const promises = categories.map(category => 
      generatePersonalizedMessage(category, numerologyData, userData)
        .then(message => ({ category, message }))
        .catch(error => ({ 
          category, 
          message: `⚠️ Error al generar mensaje de ${category}` 
        }))
    );
    
    const results = await Promise.all(promises);
    
    // Convertir array a objeto { category: message }
    return results.reduce((acc, { category, message }) => {
      acc[category] = message;
      return acc;
    }, {});
    
  } catch (error) {
    console.error('❌ Error generando mensajes en batch:', error);
    throw error;
  }
}

/**
 * Generar mensaje UNIVERSAL (compartido) para una categoría y un día universal.
 * No requiere autenticación: usado por la página pública /universal.
 * @param {string} category - 'work' | 'love' | 'wellness' | 'spiritual'
 * @param {number} universalDay - Número universal del día (1-9, 11, 22)
 * @returns {Promise<string>} - Mensaje generado
 */
export async function generateUniversalMessage(category, universalDay) {
  const response = await fetch(`${API_BASE_URL}/messages/generate-universal`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ category, universalDay })
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Error ${response.status} generando mensaje universal: ${text}`);
  }

  const data = await response.json();

  if (!data || data.success !== true || typeof data.message !== 'string') {
    throw new Error(data?.error || 'Respuesta inválida del backend universal');
  }

  console.log(`✅ Mensaje universal ${category}:`, {
    cached: !!data.cached,
    tokens: data.tokensUsed,
    model: data.model
  });

  return data.message;
}

/**
 * Generar mensajes UNIVERSALES en paralelo para varias categorías.
 * @param {Array<string>} categories
 * @param {number} universalDay
 * @returns {Promise<Record<string, string>>}
 */
export async function generateBatchUniversalMessages(categories, universalDay) {
  const results = await Promise.all(
    categories.map((category) =>
      generateUniversalMessage(category, universalDay)
        .then((message) => ({ category, message }))
        .catch((error) => {
          console.error(`❌ Error universal ${category}:`, error);
          return {
            category,
            message: `⚠️ No pudimos generar el mensaje universal de ${category} en este momento.`
          };
        })
    )
  );

  return results.reduce((acc, { category, message }) => {
    acc[category] = message;
    return acc;
  }, {});
}

/**
 * Limpiar caché de mensajes (para testing)
 * @param {string} date - Fecha en formato YYYY-MM-DD
 */
export async function clearCache(date) {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/clear-cache`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify({ date })
    });
    
    if (!response.ok) {
      throw new Error('Error clearing cache');
    }
    
    const data = await response.json();
    console.log('🗑️ Caché limpiado:', data);
    return data;
    
  } catch (error) {
    console.error('❌ Error limpiando caché:', error);
    throw error;
  }
}

/**
 * Obtener estadísticas del caché
 */
export async function getCacheStats() {
  try {
    const response = await fetch(`${API_BASE_URL}/messages/stats`, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Error getting cache stats');
    }
    
    const data = await response.json();
    return data;
    
  } catch (error) {
    console.error('❌ Error obteniendo stats:', error);
    return {
      totalMessages: 0,
      totalTokens: 0,
      totalCost: 0
    };
  }
}

/**
 * Verificar estado del backend
 */
export async function checkBackendHealth() {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch (error) {
    console.error('❌ Backend no disponible:', error);
    return false;
  }
}

// ==================== Pinnacle API ====================

async function pinnacleRequest(path = "", { method = "GET", body } = {}) {
  const token = getAuthToken();
  if (!token) {
    const error = new Error("No autenticado");
    error.status = 401;
    throw error;
  }

  const headers = {
    "Authorization": `Bearer ${token}`
  };
  if (body) {
    headers["Content-Type"] = "application/json";
  }

  const response = await fetch(`${API_BASE_URL}/pinnacle${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const error = new Error(payload?.message || `Error ${response.status}`);
    error.status = response.status;
    throw error;
  }

  return payload?.data ?? payload;
}

export async function fetchPinnacleRecord({ profileType, profileId }) {
  const params = new URLSearchParams({
    profileType,
    profileId
  });
  return pinnacleRequest(`?${params.toString()}`, { method: "GET" });
}

export async function generatePinnacleRecord({ profileType, profileId }) {
  return pinnacleRequest("", {
    method: "POST",
    body: { profileType, profileId }
  });
}

export async function fetchOrCreatePinnacle({ profileType, profileId }) {
  try {
    return await fetchPinnacleRecord({ profileType, profileId });
  } catch (error) {
    if (error.status === 404) {
      return generatePinnacleRecord({ profileType, profileId });
    }
    throw error;
  }
}

// ==================== Ninja Profiles API ====================

export async function listNinjaProfiles() {
  const resp = await fetch(`${API_BASE_URL}/ninja/list`, {
    headers: { 'Authorization': `Bearer ${getAuthToken()}` }
  });
  if (!resp.ok) throw new Error('Error listando perfiles ninja');
  return resp.json();
}

// ==================== Admin Ninja API ====================

export async function adminCheckUserData(email, adminPassword) {
  const resp = await fetch(`${API_BASE_URL}/admin/check-user-data`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminPassword}` },
    body: JSON.stringify({ email })
  });
  if (!resp.ok) throw new Error('Error verificando datos de usuario');
  return resp.json();
}

export async function adminSetNinjaLimit(email, max, adminPassword) {
  const resp = await fetch(`${API_BASE_URL}/admin/set-ninja-limit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminPassword}` },
    body: JSON.stringify({ email, max })
  });
  if (!resp.ok) throw new Error('Error seteando límite ninja');
  return resp.json();
}

export async function adminCreateNinjaForEmail(payload, adminPassword) {
  const resp = await fetch(`${API_BASE_URL}/admin/create-ninja-for-email`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminPassword}` },
    body: JSON.stringify(payload)
  });
  if (!resp.ok) throw new Error('Error creando ninja por email');
  return resp.json();
}

export async function adminDeleteNinja(ninjaId, adminPassword) {
  const resp = await fetch(`${API_BASE_URL}/admin/delete-ninja`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${adminPassword}` },
    body: JSON.stringify({ ninjaId })
  });
  if (!resp.ok) throw new Error('Error eliminando ninja');
  return resp.json();
}

export async function createNinjaProfile(payload) {
  const resp = await fetch(`${API_BASE_URL}/ninja/create`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
    body: JSON.stringify({
      name: payload.name,
      apellido_paterno: payload.apellidoPaterno || null,
      apellido_materno: payload.apellidoMaterno || null,
      birth_date: payload.birth,
      occupation: payload.occupation || [],
      status: payload.status || null
    })
  });
  if (!resp.ok) throw new Error('Error creando perfil ninja');
  return resp.json();
}

export async function deleteNinjaProfile(id) {
  const resp = await fetch(`${API_BASE_URL}/ninja/delete?id=${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${getAuthToken()}` }
  });
  if (!resp.ok) throw new Error('Error eliminando perfil ninja');
  return resp.json();
}

export async function activateNinjaProfile(id) {
  const resp = await fetch(`${API_BASE_URL}/ninja/activate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
    body: JSON.stringify({ id })
  });
  if (!resp.ok) throw new Error('Error activando perfil ninja');
  return resp.json();
}

export async function updateNinjaProfile(payload) {
  const resp = await fetch(`${API_BASE_URL}/ninja/update`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${getAuthToken()}` },
    body: JSON.stringify({
      id: payload.id,
      name: payload.name,
      apellido_paterno: payload.apellidoPaterno || null,
      apellido_materno: payload.apellidoMaterno || null,
      birth_date: payload.birth,
      occupation: payload.occupation || [],
      status: payload.status || null
    })
  });
  if (!resp.ok) throw new Error('Error actualizando perfil ninja');
  return resp.json();
}

// ========================================
// PAYPAL PAYMENTS API
// ========================================

/**
 * Crear orden de pago en PayPal
 * @param {string} planId - ID del plan
 * @param {string} billingCycle - 'monthly' o 'annual'
 * @returns {Promise<{success: boolean, orderId: string}>}
 */
export async function createPayPalOrder(planId, billingCycle) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/payments/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ planId, billingCycle })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al crear orden de pago');
    }

    console.log('✅ Orden PayPal creada:', data.orderId);
    return data;
  } catch (error) {
    console.error('❌ Error creando orden PayPal:', error);
    throw error;
  }
}

/**
 * Capturar pago de PayPal (completar transacción)
 * @param {string} orderId - ID de la orden de PayPal
 * @returns {Promise<{success: boolean, subscription: object}>}
 */
export async function capturePayPalOrder(orderId) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/payments/capture-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ orderId })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al procesar el pago');
    }

    console.log('✅ Pago capturado exitosamente');
    return data;
  } catch (error) {
    console.error('❌ Error capturando pago PayPal:', error);
    throw error;
  }
}

/**
 * Crear suscripción recurrente en PayPal
 * @param {string} planId - ID del plan
 * @param {string} billingCycle - 'monthly' o 'annual'
 * @returns {Promise<{success: boolean, subscriptionId: string, approvalUrl: string}>}
 */
export async function createPayPalSubscription(planId, billingCycle) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/subscriptions/create-paypal-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ planId, billingCycle })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al crear suscripción');
    }

    console.log('✅ Suscripción PayPal creada:', data.subscriptionId);
    return data;
  } catch (error) {
    console.error('❌ Error creando suscripción PayPal:', error);
    throw error;
  }
}

// =============================================
// NINJA PROFILES - COMPRA ADICIONAL
// =============================================

/**
 * Crear suscripción recurrente de perfiles Ninja adicionales (PayPal Subscriptions)
 * @param {object} params - { quantity: number, billingCycle: 'monthly'|'annual' }
 * @returns {Promise<{success: boolean, subscriptionId: string, approvalUrl: string}>}
 */
export async function createNinjaSubscription({ quantity, billingCycle }) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ninja/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ quantity, billingCycle })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al crear suscripción');
    }

    console.log('✅ Suscripción de perfiles Ninja creada:', data.subscriptionId);
    return data;
  } catch (error) {
    console.error('❌ Error creando suscripción de perfiles Ninja:', error);
    throw error;
  }
}

/**
 * Crear orden PayPal para compra de perfiles Ninja adicionales (LEGACY - one-time payment)
 * @param {object} params - { quantity: number, billingCycle: 'monthly'|'annual' }
 * @returns {Promise<{success: boolean, orderId: string, approvalUrl: string, price: object}>}
 */
export async function purchaseAdditionalNinjas({ quantity, billingCycle }) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ninja/purchase-additional`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ quantity, billingCycle })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al crear orden de compra');
    }

    console.log('✅ Orden de compra de perfiles Ninja creada:', data.orderId);
    return data;
  } catch (error) {
    console.error('❌ Error creando orden de perfiles Ninja:', error);
    throw error;
  }
}

/**
 * Capturar pago de compra de perfiles Ninja adicionales
 * @param {object} params - { orderId: string }
 * @returns {Promise<{success: boolean, purchase: object, limits: object}>}
 */
export async function captureNinjaPurchase({ orderId }) {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ninja/capture-purchase`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ orderId })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al procesar la compra');
    }

    console.log('✅ Compra de perfiles Ninja procesada exitosamente');
    return data;
  } catch (error) {
    console.error('❌ Error procesando compra de perfiles Ninja:', error);
    throw error;
  }
}

/**
 * Obtener historial de compras de perfiles Ninja
 * @returns {Promise<{success: boolean, purchases: array, stats: object}>}
 */
export async function getNinjaPurchaseHistory() {
  try {
    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/ninja/purchase-history`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || 'Error al obtener historial');
    }

    console.log('✅ Historial de compras obtenido');
    return data;
  } catch (error) {
    console.error('❌ Error obteniendo historial de compras:', error);
    throw error;
  }
}

